
import { Component, Input, Output, EventEmitter, inject } from '@angular/core';
import { UntypedFormGroup, UntypedFormArray, UntypedFormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Loader } from '@aston/foundation';
import { take } from 'rxjs/operators';
import { AppConstants } from 'apps/console/src/app/app.constants';
import { TranslateModule } from '@ngx-translate/core';

import { FileUploadService } from '../../services';
import { IFileInfo, IFileUploadConfig } from '../../models';

@Component({
	selector: 'file-upload',
	templateUrl: './file-upload.component.html',
	styles: `.files-list-selected li { width: 420px; }`,
	imports: [TranslateModule, FormsModule, ReactiveFormsModule]
})
export class FileUploadComponent {
	private fileUploadService = inject(FileUploadService);

	@Input() form: UntypedFormGroup | UntypedFormArray;

	@Input() readonly = false;
	@Input() isMulti = false;
	@Input() delegateUpload = true;
	@Input() alignLeft = false;
	@Input() buttonKey = 'Actions.UploadFile';
	@Input() config: IFileUploadConfig;

	@Output() delete = new EventEmitter<UntypedFormGroup[]>();
	@Output() selection = new EventEmitter<void>();

	get accept(): string {
		const acceptExtensions = this.config.acceptExtensions || AppConstants.FILE_UPLOAD_CONFIGS.DEFAULT.acceptExtensions;
		return acceptExtensions.map(e => '.' + e).join(', ');
	}

	get showAddButton(): boolean {
		return this.isMulti || !this.form.get('fileInfo').value;
	}

	get formGroup(): FormGroup {
		// ugly linting workaround until refactoring
		return this.form as FormGroup;
	}

	get files(): UntypedFormGroup[] {
		if (!this.isMulti) {
			return [this.form as UntypedFormGroup];
		}

		const files = this.form as UntypedFormArray;
		return files.controls as UntypedFormGroup[];
	}

	get isLoading() {
		return this.files
			.some(file => {
				const loader = file.get('fileLoader').value as Loader;
				return loader.isLoading;
			});
	}

	onChange(event: Event) {
		const target = event.target as HTMLInputElement;
		if (!this.isMulti) {
			const file = target.files.item(0);
			const fileControl = this.form as UntypedFormGroup;
			if (this.delegateUpload) {
				fileControl.patchValue({
					underlyingFile: file
				});
				target.value = null;
				return;
			}
			this.fileUploadService.checkFile(file, this.config).pipe(take(1)).subscribe(allowed => {
				if (allowed) {
					fileControl.patchValue({
						fileInfo: this.getFileInfo(file),
						underlyingFile: file
					});
				}
				target.value = null;
			});
		} else {
			// eslint-disable-next-line @typescript-eslint/prefer-for-of
			for (let i = 0; i < target.files.length; i++) {
				const file = target.files[i];
				const files = this.form as UntypedFormArray;
				if (this.delegateUpload) {
					files.push(new UntypedFormGroup({
						fileInfo: new UntypedFormControl(null),
						underlyingFile: new UntypedFormControl(file),
						fileLoader: new UntypedFormControl(new Loader())
					}));
					target.value = null;
					return;
				}
				this.fileUploadService.checkFile(file, this.config).pipe(take(1)).subscribe(allowed => {
					if (allowed) {
						files.push(new UntypedFormGroup({
							fileInfo: new UntypedFormControl(this.getFileInfo(file)),
							underlyingFile: new UntypedFormControl(file),
							fileLoader: new UntypedFormControl(new Loader())
						}));
					}
					target.value = null;
				});
			}
		}
		this.selection.emit();
	}

	deleteFile(index: number) {
		if (!this.isMulti) {
			const value: any = {
				underlyingFile: null
			};
			if (!this.delegateUpload) {
				value.fileInfo = null;
			}
			const file = this.form as UntypedFormGroup;
			file.patchValue(value);
		} else {
			const files = this.form as UntypedFormArray;
			files.removeAt(index);
		}
		this.delete.emit(this.files)
	}

	private getFileInfo(file: File): IFileInfo {
		return {
			name: file.name,
			type: file.type,
			size: file.size
		};
	}
}
